"use client"

import {
  classNames,
  Media,
  tv,
  useIsGreaterThanOrEqualToLg,
} from "@opensea/ui-kit"
import { useIntersected } from "@opensea/ui-kit/hooks"
import type React from "react"
import { useRef } from "react"
import { useContextSelector } from "use-context-selector"
import { TableHeaderProvider } from "@/providers/TableHeaderProvider"
import { type TopStickyVariant, topStickyVariants } from "@/styles/utils/sticky"
import { useItemView } from "../items/ItemViewProvider"
import { useUpdateTopNavHeight } from "../navigation/top-nav/hooks"
import {
  type FilterDefaults,
  FilterDefaultsProvider,
} from "./FilterDefaultsProvider"
import {
  FilterLayoutContext,
  FilterLayoutProvider,
} from "./FilterLayoutProvider"
import { FilterSidePanelProvider } from "./FilterSidePanelProvider"
import { SideModuleProvider } from "./SideModuleProvider"
import type { FilterLayoutPage } from "./types"

const filterLayoutVariants = tv({
  slots: {
    container: "flex",
    filterSidePanelContainer: "",
    mainContainer: "flex min-w-0 grow-1 flex-col",
    sideModuleContainer: "",
  },
  variants: {
    isFilterSidePanelOpen: {
      true: "",
    },
    isSideModuleOpen: {
      true: "",
    },
    sideColumnSize: {
      sm: "",
      md: "",
      lg: "",
    },
  },
  compoundVariants: [
    {
      isFilterSidePanelOpen: false,
      isSideModuleOpen: false,
      className: { container: "" },
    },

    // e.g. For stats page
    {
      isFilterSidePanelOpen: false,
      isSideModuleOpen: true,
      sideColumnSize: "sm",
      class: {
        container:
          "3xl:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,17fr)_minmax(0,7fr)] 2xl:grid-cols-[minmax(0,18fr)_minmax(0,6fr)]",
      },
    },
    {
      isFilterSidePanelOpen: true,
      isSideModuleOpen: false,
      sideColumnSize: "sm",
      class: {
        container:
          "3xl:grid-cols-[270px_minmax(0,1fr)] xl:grid-cols-[minmax(0,5fr)_minmax(0,19fr)] 2xl:grid-cols-[minmax(0,5fr)_minmax(0,19fr)]",
      },
    },
    {
      isFilterSidePanelOpen: true,
      isSideModuleOpen: true,
      sideColumnSize: "sm",
      class: {
        container:
          "3xl:grid-cols-[270px_minmax(0,1fr)_270px] 2xl:grid-cols-[minmax(0,5fr)_minmax(0,13fr)_minmax(0,6fr)]",
      },
    },

    // e.g. For collection page
    {
      isFilterSidePanelOpen: false,
      isSideModuleOpen: true,
      sideColumnSize: "md",
      class: {
        container:
          "3xl:grid-cols-[minmax(0,13fr)_minmax(0,11fr)] xl:grid-cols-[minmax(0,17fr)_minmax(0,7fr)] 2xl:grid-cols-[minmax(0,17fr)_minmax(0,7fr)]",
      },
    },
    {
      isFilterSidePanelOpen: true,
      isSideModuleOpen: false,
      sideColumnSize: "md",
      class: {
        container:
          "3xl:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[180px_minmax(0,17fr)] 2xl:grid-cols-[220px_minmax(0,19fr)]",
      },
    },
    {
      isFilterSidePanelOpen: true,
      isSideModuleOpen: true,
      sideColumnSize: "md",
      class: {
        container:
          "3xl:grid-cols-[280px_minmax(0,13fr)_minmax(0,7fr)] 4xl:grid-cols-[280px_minmax(0,13fr)_minmax(0,7fr)] xl:grid-cols-[180px_minmax(0,13fr)_minmax(0,7fr)] 2xl:grid-cols-[220px_minmax(0,13fr)_minmax(0,7fr)]",
      },
    },

    // e.g. For profile page
    {
      isFilterSidePanelOpen: false,
      isSideModuleOpen: true,
      sideColumnSize: "lg",
      class: {
        container:
          "3xl:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,16fr)_minmax(0,8fr)] 2xl:grid-cols-[minmax(0,17fr)_minmax(0,7fr)]",
      },
    },
    {
      isFilterSidePanelOpen: true,
      isSideModuleOpen: false,
      sideColumnSize: "lg",
      class: {
        container:
          "3xl:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[minmax(0,6fr)_minmax(0,18fr)] 2xl:grid-cols-[minmax(0,6fr)_minmax(0,18fr)]",
      },
    },
    {
      isFilterSidePanelOpen: true,
      isSideModuleOpen: true,
      sideColumnSize: "lg",
      class: {
        container:
          "3xl:grid-cols-[380px_minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,6fr)_minmax(0,11fr)_minmax(0,7fr)]",
      },
    },
  ],
})

export type FilterLayoutProps = {
  toolbar?: React.ReactNode
  main: React.ReactNode
  filterSidePanel?: React.ReactNode
  filterSheet?: React.ReactNode
  sideModuleSheet?: React.ReactNode
  sideModule?: React.ReactNode
  initialIsFilterSidePanelOpen?: boolean
  initialIsSideModuleOpen?: boolean
  filterDefaults?: FilterDefaults
  sideColumnSize?: "sm" | "md" | "lg"
  topStickyVariant?: TopStickyVariant
  page: FilterLayoutPage

  className?: string
  style?: React.CSSProperties | undefined

  contentClassName?: string
  toolbarUnderlineClassName?: string

  overrides?: {
    TableHeaderProvider?: {
      className?: string
    }
    mainContainer?: {
      className?: string
    }
    filterSidePanelContainer?: {
      className?: string
    }
    filterSidePanelStickyContainer?: {
      className?: string
    }
    sideModuleContainer?: {
      className?: string
    }

    toolbarContainer?: {
      className?: string
    }

    toolbarUnderlineClassName?: string
    isSideModuleOpen?: boolean
    isFilterSidePanelOpen?: boolean
    toolbarStickyBorder?: boolean
  }
}

function FilterLayoutBase({
  toolbar,
  main,
  filterSidePanel,
  filterSheet,
  sideModuleSheet,
  sideModule,
  topStickyVariant,
  sideColumnSize = "md",
  className,
  style,
  contentClassName,
  toolbarUnderlineClassName,
  overrides,
  page,
}: Omit<FilterLayoutProps, "itemView">) {
  const { itemViewGroup } = useItemView()
  const isAsidePadding = ![
    "stats",
    "studio",
    "activity",
    "watchlist",
    "token-watchlist",
    "token",
  ].includes(page)

  const [isFilterSidePanelOpenContext, isSideModuleOpenContext] =
    useContextSelector(FilterLayoutContext, v => [
      v.isFilterSidePanelOpen,
      v.isSideModuleOpen,
    ])

  const isFilterSidePanelOpen =
    Boolean(filterSidePanel) &&
    (overrides?.isFilterSidePanelOpen || isFilterSidePanelOpenContext)

  const isSideModuleOpen =
    Boolean(sideModule) &&
    (overrides?.isSideModuleOpen || isSideModuleOpenContext)

  const {
    container,
    filterSidePanelContainer,
    mainContainer,
    sideModuleContainer,
  } = filterLayoutVariants({
    isFilterSidePanelOpen,
    isSideModuleOpen,
    sideColumnSize,
  })

  useUpdateTopNavHeight(
    (topNavHeight, topNavOffset) => {
      if (!toolbarRef.current) {
        return
      }

      const { top } = toolbarRef.current.getBoundingClientRect()
      if (Math.round(top) > topNavHeight + topNavOffset) {
        return
      }

      return topNavHeight + 80
    },
    [itemViewGroup],
  )

  const toolbarRef = useRef<HTMLDivElement>(null)
  const contentParentRef = useRef<HTMLDivElement>(null)

  // Header utils
  const isScrolledDown = useIntersected(contentParentRef, toolbarRef)
  const showToolbarUnderline = isScrolledDown && overrides?.toolbarStickyBorder
  const showTableHeaderBorder = useIsGreaterThanOrEqualToLg()

  const topStickyClassName = topStickyVariants({
    variant: topStickyVariant ?? "after-top-nav",
  })

  return (
    <>
      <div className={classNames(container(), className)} style={style}>
        <Media greaterThanOrEqual="xl">
          {isFilterSidePanelOpen ? (
            <aside
              className={classNames(
                "w-[320px] shrink-0",
                isAsidePadding && "pr-4 lg:pr-6",
              )}
            >
              <div
                className={classNames(
                  topStickyClassName,
                  overrides?.filterSidePanelStickyContainer?.className,
                )}
              >
                <div
                  className={classNames(
                    filterSidePanelContainer(),
                    overrides?.filterSidePanelContainer?.className,
                  )}
                >
                  {filterSidePanel}
                </div>
              </div>
            </aside>
          ) : null}
        </Media>
        <div
          className={classNames(
            mainContainer(),
            overrides?.mainContainer?.className,
          )}
        >
          {toolbar ? (
            <div
              className={classNames(
                topStickyClassName,
                "z-toolbar",
                overrides?.toolbarContainer?.className,
              )}
              id="filter-layout-toolbar"
              ref={toolbarRef}
            >
              <div
                className={classNames(
                  showToolbarUnderline &&
                    (toolbarUnderlineClassName ??
                      "bg-bg-app *:inset-shadow-border *:shadow-[0_1px_0] *:transition-shadow"),
                )}
              >
                {toolbar}
              </div>
            </div>
          ) : null}

          <TableHeaderProvider
            borderOnScroll={showTableHeaderBorder}
            className={classNames(
              "z-top-navbar",
              overrides?.TableHeaderProvider?.className,
            )}
          >
            <div
              className={classNames("h-full", contentClassName)}
              ref={contentParentRef}
            >
              {main}
            </div>
          </TableHeaderProvider>
        </div>

        <Media greaterThanOrEqual="xl">
          {isSideModuleOpen ? (
            <div>
              <div className={topStickyClassName}>
                <div
                  className={classNames(
                    sideModuleContainer(),
                    overrides?.sideModuleContainer?.className,
                  )}
                >
                  {sideModule}
                </div>
              </div>
            </div>
          ) : null}
        </Media>
      </div>

      {filterSheet}
      {sideModuleSheet}
    </>
  )
}

const openToState = (open?: boolean) => {
  if (open === undefined) {
    return undefined
  }

  return open ? "open" : "closed"
}

export function FilterLayout({
  initialIsFilterSidePanelOpen,
  initialIsSideModuleOpen,
  filterDefaults,
  page,
  ...rest
}: FilterLayoutProps) {
  return (
    <FilterDefaultsProvider {...filterDefaults}>
      <FilterSidePanelProvider
        filterSidePanelState={openToState(initialIsFilterSidePanelOpen)}
        page={page}
      >
        <SideModuleProvider
          page={page}
          sideModuleState={openToState(initialIsSideModuleOpen)}
        >
          <FilterLayoutProvider page={page}>
            <FilterLayoutBase page={page} {...rest} />
          </FilterLayoutProvider>
        </SideModuleProvider>
      </FilterSidePanelProvider>
    </FilterDefaultsProvider>
  )
}
